import {Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy} from '@angular/core';
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownTriggerDirective,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {NgClass} from "@angular/common";
import {faChevronDown, faFilter} from "@fortawesome/pro-solid-svg-icons";

export interface BicFilterConfig {
  key: string;
  displayName: string;
  type: 'text' | 'date' | 'status' | 'filesize' | string;
  getOptions?: () => any[];
  options?: any[];
  formatOption?: (value: any) => string;
  pipe?: any;
}

@Component({
  selector: 'app-bic-filter',
  imports: [
    BicButtonComponent,
    BicDropdownComponent,
    FaIconComponent,
    BicDropdownTriggerDirective,
    NgClass,
    TooltipDirective
  ],
  templateUrl: './bic-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bic-filter.component.scss'
})
export class BicFilterComponent implements OnChanges {
  @Input() filtersConfig: BicFilterConfig[] = [];
  /**
   * Zusätzliche Klasse für das Text-Label ("Filter (n)"). Default zeigt das Label immer (bisheriges
   * Verhalten); Aufrufer mit engen Topbars können hier `bic-btn-label--compact` setzen, um unterhalb
   * des in styles.scss definierten Breakpoints auf Icon-only zu wechseln (Tooltip trägt dann die
   * Bedeutung). Der globale `:has()`-Selektor in styles.scss blendet dabei auch das leere
   * bic-button-interne Wrapper-Div aus – sonst bliebe dessen `gap` als Lücke rechts vom Icon stehen.
   */
  @Input() labelClass = '';
  expandedAccordionColumn: string | null = null;

  @Output() filtersChange = new EventEmitter<{ [key: string]: any[] }>();

  activeFilters: { [key: string]: any[] } = {};
  protected readonly faChevronDown = faChevronDown;
  protected readonly faFilter = faFilter;

  private readonly optionsCache = new Map<string, any[]>();
  private readonly labelCache = new Map<string, string>();

  /**
   * Optionen einer Spalte, gecacht bis das Dropdown wieder geschlossen wird.
   *
   * `col.getOptions()` sammelt die Werte ueber den gesamten Datenbestand (bei den Dateien z.B. ein
   * `flatMap` ueber alle documentClassTitles). Aufgerufen wird die Methode aus dem Template, also
   * bei JEDEM Change-Detection-Zyklus - und weil sie jedes Mal ein neues Array liefert, musste
   * Angular die `@for`-Liste zusaetzlich jedes Mal neu abgleichen. Das war die Ursache dafuer, dass
   * sich der Filter bei vielen Dateien zaeh anfuehlte.
   */
  getOptions(col: BicFilterConfig): any[] {
    const cached = this.optionsCache.get(col.key);
    if (cached) return cached;

    const options = col.options ?? (col.getOptions ? col.getOptions() : []);
    this.optionsCache.set(col.key, options);
    return options;
  }

  /** Ebenfalls gecacht: das Template ruft es zweimal pro Option (Text + Tooltip) und Zyklus. */
  formatOption(col: BicFilterConfig, value: any): string {
    const cacheKey = `${col.key}|${value}`;
    const cached = this.labelCache.get(cacheKey);
    if (cached !== undefined) return cached;

    let label: string;
    if (col.formatOption) {
      label = col.formatOption(value);
    } else if (col.pipe) {
      label = col.pipe.transform ? col.pipe.transform(value) : value;
    } else {
      label = value?.toString() || '';
    }

    this.labelCache.set(cacheKey, label);
    return label;
  }

  /** Beim Schliessen verwerfen, damit beim naechsten Oeffnen der aktuelle Datenbestand zaehlt. */
  onDropdownClosed(): void {
    this.expandedAccordionColumn = null;
    this.invalidateCaches();
  }

  ngOnChanges(): void {
    this.invalidateCaches();
  }

  private invalidateCaches(): void {
    this.optionsCache.clear();
    this.labelCache.clear();
  }

  toggleFilterValue(col: BicFilterConfig, value: any) {
    if (!this.activeFilters[col.key]) this.activeFilters[col.key] = [];
    const idx = this.activeFilters[col.key].indexOf(value);
    if (idx > -1) {
      this.activeFilters[col.key].splice(idx, 1);
    } else {
      this.activeFilters[col.key].push(value);
    }
    this.filtersChange.emit({...this.activeFilters});
  }

  toggleColumnAccordion(columnName: string): void {
    if (this.expandedAccordionColumn === columnName) {
      this.expandedAccordionColumn = null;
    } else {
      this.expandedAccordionColumn = columnName;
    }
  }

  clearAll() {
    this.activeFilters = {};
    this.filtersChange.emit({});
  }

  isSelected(col: BicFilterConfig, value: any): boolean {
    return this.activeFilters[col.key]?.includes(value);
  }

  getSelectedCountLabel(key: string): string {
    return this.activeFilters[key]?.length ? ' (' + this.activeFilters[key].length + ')' : '';
  }

  getFilterCount() {
    return Object.values(this.activeFilters).reduce((sum, arr) => sum + arr.length, 0);
  }
}
